"use client";

import Image from "next/image";
import { useLanguage } from "@/contexts/LanguageContext";

// ── Types ──────────────────────────────────────────────

type Language = "en" | "es";

type Bilingual = Record<Language, string>;

interface FounderStat {
  value: string;
  label: Bilingual;
}

interface FounderBio {
  name: string;
  title: Bilingual;
  summary: Bilingual;
  photo: string;
  stats: FounderStat[];
}

interface ExperienceRole {
  company: string;
  url?: string;
  logo?: string;
  role: Bilingual;
  industry: Bilingual;
  duration: Bilingual;
  technologies: string[];
  concepts?: string[];
  highlight: Bilingual;
}

interface ConsultingClient {
  name: string;
  url?: string;
  domain: Bilingual;
}

interface ConsultingGroup {
  label: Bilingual;
  description: Bilingual;
  clients: ConsultingClient[];
}

interface ExperienceContent {
  founder: FounderBio;
  roles: ExperienceRole[];
  consulting: ConsultingGroup;
}

// ── Helpers ────────────────────────────────────────────

function getDomain(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
}

/** Maps English month abbreviations + "Present"/"Ongoing" to ISO-friendly values. */
const MONTH_MAP: Record<string, string> = {
  Jan: "01",
  Feb: "02",
  Mar: "03",
  Apr: "04",
  May: "05",
  Jun: "06",
  Jul: "07",
  Aug: "08",
  Sep: "09",
  Oct: "10",
  Nov: "11",
  Dec: "12",
};

/**
 * Parses a display date like "Sep 2023" into ISO "2023-09".
 * Returns undefined for empty strings, "Present", "Ongoing", etc.
 */
function toDatetimeAttr(display: string): string | undefined {
  const trimmed = display.trim();
  if (!trimmed || /present|ongoing|en curso|presente/i.test(trimmed)) {
    return undefined;
  }
  const match = trimmed.match(/^(\w{3})\s+(\d{4})$/);
  if (match) {
    const month = MONTH_MAP[match[1]];
    if (month) return `${match[2]}-${month}`;
  }
  return undefined;
}

/**
 * Renders a date string as a <time> element when an ISO value can be derived,
 * otherwise renders a plain <span>.
 */
function DateDisplay({
  display,
  className,
}: {
  display: string;
  className?: string;
}) {
  const datetime = toDatetimeAttr(display);
  if (datetime) {
    return (
      <time dateTime={datetime} className={className}>
        {display}
      </time>
    );
  }
  return <span className={className}>{display}</span>;
}

/**
 * Renders a duration like "Sep 2023 – Present" with <time> tags for each part.
 */
function DurationDisplay({
  duration,
  className,
}: {
  duration: string;
  className?: string;
}) {
  const parts = duration.split(/\s*[–—-]\s*/);
  if (parts.length === 2) {
    return (
      <span className={className}>
        <DateDisplay display={parts[0]} />
        {" – "}
        <DateDisplay display={parts[1]} />
      </span>
    );
  }
  return <DateDisplay display={duration} className={className} />;
}

// ── Data ───────────────────────────────────────────────

const EXPERIENCE_DATA: ExperienceContent = {
  founder: {
    name: "Ruben Quispe",
    title: {
      en: "AI-Augmented Cloud Architect",
      es: "Arquitecto Cloud Aumentado con IA",
    },
    summary: {
      en: "I build the tools that make AI agents actually useful for infrastructure work. From Engram (persistent memory for AI coding agents) to Spec-Driven Development (a methodology for AI-assisted software engineering), my open source work focuses on the intersection of AI agents and cloud architecture. Under the hood: 6+ years of enterprise cloud infrastructure across AWS, GCP, and Azure for companies in Latin America.",
      es: "Construyo las herramientas que hacen que los agentes de IA sean realmente útiles para el trabajo de infraestructura. Desde Engram (memoria persistente para agentes de código IA) hasta Spec-Driven Development (una metodología para ingeniería de software asistida por IA), mi trabajo open source se enfoca en la intersección de agentes de IA y arquitectura cloud. Por debajo: 6+ años de infraestructura cloud empresarial en AWS, GCP y Azure para empresas en Latinoamérica.",
    },
    photo: "/images/ruben-quispe.jpg",
    stats: [
      {
        value: "3",
        label: {
          en: "Open Source AI Tools",
          es: "Herramientas IA Open Source",
        },
      },
      {
        value: "8+",
        label: {
          en: "AI Agents Supported",
          es: "Agentes IA Soportados",
        },
      },
      {
        value: "6+",
        label: {
          en: "Years in Cloud & DevOps",
          es: "Años en Cloud & DevOps",
        },
      },
    ],
  },
  roles: [
    {
      company: "CloudOps Studio",
      url: "https://cloudopstudio.com/",
      logo: "/images/cloudops-icon.png",
      role: {
        en: "Founder — AI-Augmented Cloud Consulting",
        es: "Fundador — Consultoría Cloud Aumentada con IA",
      },
      industry: {
        en: "AI Agent Tooling & Cloud Infrastructure",
        es: "Herramientas para Agentes IA & Infraestructura Cloud",
      },
      duration: { en: "Ongoing", es: "En curso" },
      technologies: [
        "Go",
        "SQLite",
        "FTS5",
        "MCP Protocol",
        "PostgreSQL",
        "TypeScript",
        "Terraform",
        "Terragrunt",
        "AWS",
        "Azure",
        "Docker",
        "C++",
        "FreeRTOS",
        "ESP32-S3",
        "Raspberry Pi",
        "OpenCV",
        "YOLO",
        "Whisper",
        "KiCad",
        "Fusion 360",
      ],
      concepts: [
        "AI Agents",
        "MCP Protocol",
        "Spec-Driven Dev",
        "Open Source",
        "Edge AI",
        "Robotics",
        "IaC",
      ],
      highlight: {
        en: "Building open source tools for the AI agent ecosystem. Engram: persistent memory for AI coding agents (Go, SQLite+FTS5, MCP protocol) — agent-agnostic, supports Claude, OpenCode, Gemini, Codex, Cursor, Windsurf, with cloud sync to PostgreSQL for teams. Gentle AI (gga): one command to configure any AI coding agent on any OS, adapter pattern for 8+ agents, SDD workflow, skills, and personas. Spec-Driven Development (SDD): structured methodology for AI-assisted software engineering (explore → propose → spec → design → tasks → apply → verify → archive). Also building a hexapod home assistant robot with LLM agent, computer vision, and voice recognition on a dual-processor architecture (ESP32-S3 + RPi 5).",
        es: "Construyendo herramientas open source para el ecosistema de agentes IA. Engram: memoria persistente para agentes de código IA (Go, SQLite+FTS5, protocolo MCP) — agnóstico de agente, soporta Claude, OpenCode, Gemini, Codex, Cursor, Windsurf, con sincronización cloud a PostgreSQL para equipos. Gentle AI (gga): un solo comando para configurar cualquier agente de código IA en cualquier OS, patrón adapter para 8+ agentes, flujo SDD, skills y personas. Spec-Driven Development (SDD): metodología estructurada para ingeniería de software asistida por IA (explorar → proponer → especificar → diseñar → tareas → aplicar → verificar → archivar). También construyendo un robot hexápodo asistente del hogar con agente LLM, visión computacional y reconocimiento de voz en arquitectura de doble procesador (ESP32-S3 + RPi 5).",
      },
    },
    {
      company: "Cloud Architecture Firm",
      url: "https://www.whiteliontechnology.com/",
      role: {
        en: "Lead Architecture Consultant",
        es: "Consultor Líder de Arquitectura",
      },
      industry: {
        en: "Multi-cloud enterprise solutions for LATAM",
        es: "Soluciones empresariales multi-cloud para LATAM",
      },
      duration: { en: "", es: "" },
      technologies: [
        "Azure",
        "AWS",
        "Kubernetes",
        "Docker",
        "GitHub Actions",
        "Helm",
        "ArgoCD",
        "Terraform",
        "Terragrunt",
        "Kong",
        "Backstage",
        "Vault",
        "SonarQube",
        "Trivy",
        "Checkov",
        "Prometheus",
        "Grafana",
        "CloudWatch",
        "Linux",
        ".NET",
        "React",
      ],
      concepts: [
        "AI-Assisted IaC",
        "Developer Portals",
        "Platform Engineering",
        "GitOps",
        "Zero Trust",
        "DevSecOps",
      ],
      highlight: {
        en: "Led multi-cloud infrastructure projects (AWS + Azure) for enterprise clients in LATAM. Built AI-assisted IaC pipelines with Terraform/Terragrunt and automatic log analysis. Implemented developer portals with Backstage for self-service platform engineering. Defined architecture governance, cloud infrastructure patterns, and Kubernetes-at-scale strategies for engineering teams.",
        es: "Lideró proyectos de infraestructura multi-cloud (AWS + Azure) para clientes empresariales en LATAM. Construyó pipelines de IaC asistidos por IA con Terraform/Terragrunt y análisis automático de logs. Implementó portales de desarrollador con Backstage para ingeniería de plataformas self-service. Definió gobernanza de arquitectura, patrones de infraestructura cloud y estrategias de Kubernetes a escala para equipos de ingeniería.",
      },
    },
    {
      company: "Enterprise Technology Consultancy",
      url: "https://zentagroup.com/",
      role: {
        en: "Expert DevSecOps Engineer",
        es: "Ingeniero DevSecOps Experto",
      },
      industry: {
        en: "Enterprise CI/CD & security automation",
        es: "Automatización de CI/CD y seguridad empresarial",
      },
      duration: { en: "Sep 2023 – Present", es: "Sep 2023 – Presente" },
      technologies: [
        "AWS",
        "GCP",
        "Kubernetes",
        "Docker",
        "Jenkins",
        "Helm",
        "ArgoCD",
        "Terraform",
        "Terragrunt",
        "Istio",
        "Vault",
        "SonarQube",
        "Trivy",
        "Checkov",
        "Veracode",
        "Prometheus",
        "Grafana",
        "CloudWatch",
        "Linux",
      ],
      concepts: [
        "IaC",
        "GitOps",
        "Zero Trust",
        "Shift-Left Security",
        "DevSecOps",
        "SRE",
        "DORA Metrics",
      ],
      highlight: {
        en: "CI/CD automation and security pipeline engineering for enterprise clients. Built Jenkins shared libraries, integrated shift-left security tooling (SonarQube, Trivy, Checkov, Veracode), and managed multi-cloud infrastructure across AWS and GCP with Kubernetes at scale.",
        es: "Automatización de CI/CD e ingeniería de pipelines de seguridad para clientes empresariales. Construyó bibliotecas compartidas de Jenkins, integró herramientas de seguridad shift-left (SonarQube, Trivy, Checkov, Veracode) y gestionó infraestructura multi-cloud en AWS y GCP con Kubernetes a escala.",
      },
    },
    {
      company: "Apply Digital",
      url: "https://applydigital.com/",
      role: {
        en: "Solutions Architect",
        es: "Arquitecto de Soluciones",
      },
      industry: {
        en: "Global digital consultancy",
        es: "Consultora digital global",
      },
      duration: { en: "Jul 2021 – Sep 2023", es: "Jul 2021 – Sep 2023" },
      technologies: [
        "AWS",
        "Azure",
        "Kubernetes",
        "Docker",
        "GitHub Actions",
        "Helm",
        "ArgoCD",
        "Terraform",
        "Vault",
        "Velero",
        "Istio",
        "SonarQube",
        "Prometheus",
        "Grafana",
        "CloudWatch",
        "Linux",
      ],
      concepts: ["IaC", "GitOps", "SRE"],
      highlight: {
        en: "Enterprise-scale cloud architecture and container orchestration for Fortune 500 clients across multiple industries.",
        es: "Arquitectura cloud a escala empresarial y orquestación de contenedores para clientes Fortune 500 en múltiples industrias.",
      },
    },
    {
      company: "Entel Perú",
      url: "https://www.entel.pe/",
      role: {
        en: "Access Transmission Implementation Coordinator",
        es: "Coordinador de Implementación de Transmisión de Acceso",
      },
      industry: {
        en: "Telecommunications",
        es: "Telecomunicaciones",
      },
      duration: { en: "Feb 2020 – Aug 2021", es: "Feb 2020 – Ago 2021" },
      technologies: [
        "Scrum",
        "Jira",
        "Agile",
        "Node.js",
        "React",
        "Python",
        "MongoDB",
        "Oracle",
        "REST",
      ],
      highlight: {
        en: "Coordinated development teams and managed implementation projects in the Access Transmission Engineering area. Led Scrum ceremonies, oversaw development of internal platforms for network inventory, contractor tracking, and KPI analysis in the telecommunications sector.",
        es: "Coordinó equipos de desarrollo y gestionó proyectos de implementación en el área de Ingeniería de Transmisión de Acceso. Lideró ceremonias Scrum, supervisó el desarrollo de plataformas internas para inventario de red, seguimiento de contratistas y análisis de KPIs en el sector de telecomunicaciones.",
      },
    },
  ],
  consulting: {
    label: {
      en: "Independent Consulting",
      es: "Consultoría Independiente",
    },
    description: {
      en: "Parallel consulting engagements demonstrating cross-industry breadth:",
      es: "Proyectos de consultoría paralelos que demuestran amplitud multi-industria:",
    },
    clients: [
      {
        name: "Hackmetrix",
        url: "https://hackmetrix.com/",
        domain: { en: "Cybersecurity", es: "Ciberseguridad" },
      },
      {
        name: "Autofact",
        url: "https://www.autofact.cl/",
        domain: {
          en: "Automotive / Vehicle History",
          es: "Automotriz / Historial Vehicular",
        },
      },
      {
        name: "Prodigio",
        url: "https://prodigio.tech/",
        domain: {
          en: "Technology Consulting",
          es: "Consultoría Tecnológica",
        },
      },
    ],
  },
};

// ── Component ──────────────────────────────────────────

export default function SectionExperience() {
  const { t, language } = useLanguage();

  const { founder, roles, consulting } = EXPERIENCE_DATA;

  return (
    <section
      id="owner"
      aria-label="Professional experience of Ruben Quispe Cárdenas"
      className="bg-surface py-24 md:py-32 px-6 md:px-12 text-text-primary relative"
    >
      <div className="max-w-6xl mx-auto">
        {/* Header Area — badge + title */}
        <div className="mb-16">
          <span className="inline-block py-1 px-3 rounded-full bg-brand-primary/10 text-brand-primary text-sm font-mono tracking-wider mb-4 border border-brand-primary/20">
            {t("sectionExperience.badge")}
          </span>
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight leading-tight">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-primary to-brand-secondary">
              {t("sectionExperience.title")}
            </span>
          </h2>
          <p className="text-text-secondary text-lg leading-relaxed mt-4 max-w-2xl">
            {t("sectionExperience.subtitle")}
          </p>
        </div>

        {/* Split Layout — bio left, cards right */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-12 lg:gap-16">
          {/* Left Column: Founder Bio */}
          <div className="space-y-6">
            <Image
              src={founder.photo}
              alt="Ruben Quispe — Founder of CloudOps"
              width={280}
              height={280}
              className="rounded-2xl ring-2 ring-brand-primary/30 w-full max-w-[200px] mx-auto lg:mx-0 lg:max-w-[280px] aspect-square object-cover"
            />
            <div>
              <h3 className="text-2xl font-bold text-text-primary">
                {founder.name}
              </h3>
              <p className="text-sm font-mono text-brand-primary mt-1">
                {founder.title[language]}
              </p>
            </div>
            <p className="text-text-secondary leading-relaxed text-sm">
              {founder.summary[language]}
            </p>

            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-border-default">
              {founder.stats.map((stat) => (
                <div key={stat.value}>
                  <p className="text-2xl font-bold text-text-primary">
                    {stat.value}
                  </p>
                  <p className="text-xs text-text-muted mt-1">
                    {stat.label[language]}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Experience Cards */}
          <div className="space-y-6">
            {/* Role Cards */}
            {roles.map((role) => (
              <article
                key={role.company}
                className="group p-6 rounded-2xl bg-surface-card border border-border-default hover:border-brand-secondary/30 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="text-lg font-bold text-text-primary group-hover:text-brand-secondary transition-colors">
                      {role.url ? (
                        <a
                          href={role.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 hover:text-brand-primary transition-colors"
                        >
                          {role.logo ? (
                            <Image
                              src={role.logo}
                              alt={`${role.company} logo`}
                              width={20}
                              height={20}
                              className="rounded-sm"
                            />
                          ) : (
                            // biome-ignore lint/performance/noImgElement: External favicon from Google — tiny 32px icon, no benefit from next/image optimization
                            <img
                              src={`https://www.google.com/s2/favicons?domain=${getDomain(role.url)}&sz=32`}
                              alt={`${role.company} logo`}
                              width={20}
                              height={20}
                              className="rounded-sm"
                            />
                          )}
                          {role.company}
                        </a>
                      ) : (
                        role.company
                      )}
                    </h4>
                    <p className="text-brand-primary font-mono text-sm">
                      {role.role[language]}
                    </p>
                  </div>
                  <DurationDisplay
                    duration={role.duration[language]}
                    className="text-xs text-text-muted font-mono whitespace-nowrap ml-4"
                  />
                </div>
                <p className="text-xs text-text-muted mb-3 italic">
                  {role.industry[language]}
                </p>
                <p className="text-text-secondary text-sm leading-relaxed mb-4">
                  {role.highlight[language]}
                </p>
                <div className="flex flex-wrap gap-2">
                  {role.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="text-xs font-mono bg-border-default px-2 py-1 rounded-full text-text-muted border border-border-default"
                    >
                      {tech}
                    </span>
                  ))}
                  {role.concepts?.map((concept) => (
                    <span
                      key={concept}
                      className="text-xs font-mono bg-brand-primary/10 px-2 py-1 rounded-full text-brand-primary/80 border border-brand-primary/20"
                    >
                      {concept}
                    </span>
                  ))}
                </div>
              </article>
            ))}

            {/* Consulting Engagements Card */}
            <article className="group p-6 rounded-2xl bg-surface-card border border-border-default hover:border-brand-primary/30 transition-all duration-300">
              <h4 className="text-lg font-bold text-text-primary mb-2">
                {consulting.label[language]}
              </h4>
              <p className="text-text-secondary text-sm mb-4">
                {consulting.description[language]}
              </p>
              <div className="flex flex-wrap gap-3">
                {consulting.clients.map((client) =>
                  client.url ? (
                    <a
                      key={client.name}
                      href={client.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-border-default px-3 py-2 rounded-lg border border-border-default hover:border-brand-primary/30 hover:bg-border-hover transition-all"
                    >
                      {/* biome-ignore lint/performance/noImgElement: External favicon from Google — tiny 32px icon, no benefit from next/image optimization */}
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${getDomain(client.url)}&sz=32`}
                        alt={`${client.name} logo`}
                        width={20}
                        height={20}
                        className="rounded-sm"
                      />
                      <span className="text-text-primary text-sm font-medium">
                        {client.name}
                      </span>
                      <span className="text-xs text-text-muted">
                        {client.domain[language]}
                      </span>
                    </a>
                  ) : (
                    <div
                      key={client.name}
                      className="flex items-center gap-2 bg-border-default px-3 py-2 rounded-lg border border-border-default"
                    >
                      <span className="text-text-primary text-sm font-medium">
                        {client.name}
                      </span>
                      <span className="text-xs text-text-muted">
                        {client.domain[language]}
                      </span>
                    </div>
                  ),
                )}
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
}
